import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Save, ArrowLeft, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { paymentReturnService } from '@/services/paymentReturnService'
import { billsService } from '@/services/billsService'
import { paymentsService } from '@/services/paymentsService'
import {
  PaymentReturnVm,
  PaymentReturnBill,
  PaymentReturnInsertData,
  BillVendor,
  PaymentAccount,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const PaymentReturnsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingInsertData, setLoadingInsertData] = useState(false)

  // Form state
  const [formData, setFormData] = useState<{
    vendorId: number
    billId: number
    recipientAccount: number
    destinationAccount: number
    amount: number
    date: string
    note: string
    returnType: string
  }>({
    vendorId: 0,
    billId: 0,
    recipientAccount: 0,
    destinationAccount: 0,
    amount: 0,
    date: calendarToday(),
    note: '',
    returnType: 'Cash',
  })

  // Lookup data
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [vendorBills, setVendorBills] = useState<PaymentReturnBill[]>([])
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [insertData, setInsertData] = useState<PaymentReturnInsertData | null>(null)
  const [loadedBillNo, setLoadedBillNo] = useState<string | null>(null)

  // Return type options
  const returnTypeOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank', label: 'Bank' },
  ]

  // Load vendors and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [vendorsData, accountsData] = await Promise.all([
          billsService.getAllVendors(branchId),
          paymentsService.getAllAccounts(branchId),
        ])

        setVendors(vendorsData || [])
        setAccounts(accountsData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        showSaveError(error as Error, false)
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load bills when vendor is selected (do not clear bill/amount — that wipes view/edit)
  useEffect(() => {
    if (!formData.vendorId || formData.vendorId === 0) {
      if (!isEditMode) {
        setVendorBills([])
        setInsertData(null)
      }
      return
    }

    const loadBills = async () => {
      try {
        setLoading(true)
        const includeBillId = isEditMode && formData.billId ? formData.billId : undefined
        const bills = await paymentReturnService.getBillsByVendorId(formData.vendorId, includeBillId)
        setVendorBills(bills || [])
      } catch (error) {
        console.error('Error loading bills:', error)
        await showValidationError('Failed to load bills')
      } finally {
        setLoading(false)
      }
    }

    loadBills()
  }, [formData.vendorId, isEditMode])

  // Auto-fill remaining amount only when creating a new return
  useEffect(() => {
    if (isEditMode) return

    if (!formData.billId || formData.billId === 0 || !branchId) {
      setInsertData(null)
      setFormData(prev => ({
        ...prev,
        recipientAccount: 0,
        destinationAccount: 0,
        amount: 0,
      }))
      return
    }

    const loadInsertData = async () => {
      try {
        setLoadingInsertData(true)
        const data = await paymentReturnService.getInsertData(branchId, formData.billId)
        setInsertData(data)

        if (data && data.paymentReturn) {
          setFormData(prev => ({
            ...prev,
            recipientAccount: data.paymentReturn.recipientAccount || 0,
            destinationAccount: data.paymentReturn.destinationAccount || 0,
            amount: data.paymentReturn.amount || 0,
            date: data.paymentReturn.date ? calendarFromApi(data.paymentReturn.date) || prev.date : prev.date,
            note: data.paymentReturn.note || '',
            returnType: data.paymentReturn.returnType || 'Cash',
          }))
        }
      } catch (error) {
        console.error('Error loading insert data:', error)
        await showValidationError('Failed to load payment return data')
      } finally {
        setLoadingInsertData(false)
      }
    }

    loadInsertData()
  }, [formData.billId, branchId, isEditMode])

  // Load existing payment return for view/edit
  useEffect(() => {
    if (isEditMode && id && branchId) {
      const loadPaymentReturn = async () => {
        try {
          setLoading(true)
          const paymentReturn = await paymentReturnService.getPaymentReturnById(Number(id), branchId)
          setLoadedBillNo(paymentReturn.billNo || null)

          if (paymentReturn.vendorId) {
            const bills = await paymentReturnService.getBillsByVendorId(
              paymentReturn.vendorId,
              paymentReturn.billId || undefined
            )
            setVendorBills(bills || [])
          }

          setFormData({
            vendorId: paymentReturn.vendorId || 0,
            billId: paymentReturn.billId || 0,
            recipientAccount: paymentReturn.recipientAccount || 0,
            destinationAccount: paymentReturn.destinationAccount || 0,
            amount: paymentReturn.amount || 0,
            date: paymentReturn.date ? calendarFromApi(paymentReturn.date) || calendarToday() : calendarToday(),
            note: paymentReturn.note || '',
            returnType: paymentReturn.returnType || 'Cash',
          })
        } catch (error: any) {
          console.error('Error loading payment return:', error)
          await showSaveError(error, false)
          navigate('/purchases/payment-returns')
        } finally {
          setLoading(false)
        }
      }

      loadPaymentReturn()
    }
  }, [isEditMode, id, branchId, navigate])

  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (isEditMode) {
      await showValidationError('Payment return cannot be updated.')
      return
    }

    if (!branchId) {
      await showValidationError('Please select a branch')
      return
    }

    if (!formData.vendorId || formData.vendorId === 0) {
      await showValidationError('Please select a vendor')
      return
    }

    if (!formData.billId || formData.billId === 0) {
      await showValidationError('Please select a bill')
      return
    }

    if (!formData.destinationAccount || formData.destinationAccount === 0) {
      await showValidationError('Please select destination account')
      return
    }

    if (!formData.amount || formData.amount <= 0) {
      await showValidationError('Please enter a valid amount')
      return
    }

    if (!formData.date) {
      await showValidationError('Please select a date')
      return
    }

    if (!formData.returnType) {
      await showValidationError('Please select return type')
      return
    }

    // Validate amount doesn't exceed max return amount
    if (insertData && formData.amount > insertData.maxReturnAmount) {
      await showValidationError(`Amount cannot exceed maximum return amount of ${insertData.maxReturnAmount.toFixed(2)}`)
      return
    }

    setSaving(true)

    try {
      const ipAddress = await getUserIPAddress()

      const saveData: PaymentReturnVm = {
        id: isEditMode && id ? Number(id) : 0,
        billId: formData.billId,
        vendorId: formData.vendorId,
        recipientAccount: formData.recipientAccount,
        destinationAccount: formData.destinationAccount,
        amount: formData.amount,
        date: calendarToApi(formData.date),
        note: formData.note,
        returnType: formData.returnType,
        branchId: branchId,
        ipAddress: ipAddress,
      }

      console.log('🔵 Payment Return Save Payload:', JSON.stringify(saveData, null, 2))

      if (isEditMode) {
        await paymentReturnService.updatePaymentReturn(saveData)
      } else {
        await paymentReturnService.savePaymentReturn(saveData)
      }

      await showSaveSuccess(isEditMode, isEditMode ? 'Payment return updated successfully' : 'Payment return saved successfully')
      navigate('/purchases/payment-returns')
    } catch (error: any) {
      console.error('Error saving payment return:', error)
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Failed to save payment return'
      await showSaveError(new Error(errorMessage), isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Prepare options for dropdowns
  const vendorOptions: SearchableSelectOption[] = vendors.map((vendor) => ({
    value: String(vendor.vendorId || 0),
    label: vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId}`,
  }))

  const billOptions: SearchableSelectOption[] = (() => {
    const options = vendorBills.map((bill) => ({
      value: String(bill.billId || 0),
      label: bill.billNo || `Bill ${bill.billId}`,
    }))
    if (formData.billId && !options.some((o) => o.value === String(formData.billId))) {
      options.unshift({
        value: String(formData.billId),
        label: loadedBillNo || `Bill ${formData.billId}`,
      })
    }
    return options
  })()

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountId = acc.accountId || acc.id || 0
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || ''
    const accountCode = acc.accountCode || ''
    const displayLabel = accountCode ? `${accountCode} - ${accountName}` : accountName
    return {
      value: String(accountId),
      label: displayLabel,
    }
  })

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  if (loading && !formData.vendorId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Payment Returns"
        description={isEditMode ? 'Update payment return information' : 'Create a new payment return'}
      >
        <Button variant="outline" onClick={() => navigate('/purchases/payment-returns')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Return
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Payment Return Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Vendor and Bill Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Vendor Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="vendor" className="text-gray-700 dark:text-gray-300 font-medium">
                Vendor <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={vendorOptions}
                value={String(formData.vendorId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    vendorId: Number(value),
                    billId: 0, // Reset bill when vendor changes
                  }))
                }}
                placeholder="Select vendor"
                searchPlaceholder="Search vendor..."
                emptyMessage="No vendor found"
                disabled={saving || loading || isEditMode}
              />
            </div>

            {/* Bill Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="bill" className="text-gray-700 dark:text-gray-300 font-medium">
                Bill <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={billOptions}
                value={String(formData.billId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    billId: Number(value),
                  }))
                }}
                placeholder={formData.vendorId ? "Select bill" : "Please select vendor first"}
                searchPlaceholder="Search bill..."
                emptyMessage={formData.vendorId ? "No bills found" : "Please select vendor first"}
                disabled={saving || loading || isEditMode || !formData.vendorId}
              />
              {loadingInsertData && formData.billId > 0 && (
                <p className="text-sm text-muted-foreground">Loading payment return data...</p>
              )}
            </div>
          </div>

          {/* Other Fields in Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Destination Account Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="destinationAccount" className="text-gray-700 dark:text-gray-300 font-medium">
                Destination Account <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={accountOptions}
                value={String(formData.destinationAccount)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    destinationAccount: Number(value),
                  }))
                }}
                placeholder="Select destination account"
                searchPlaceholder="Search account..."
                emptyMessage="No account found"
                disabled={saving || loading || isEditMode}
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-gray-700 dark:text-gray-300 font-medium">
                Amount <span className="text-destructive">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    amount: Number(e.target.value),
                  }))
                }}
                disabled={saving || loading || isEditMode}
                max={insertData?.maxReturnAmount}
              />
              {insertData && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Max return amount: {formatAmount(insertData.maxReturnAmount)}
                </p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-gray-700 dark:text-gray-300 font-medium">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }}
                disabled={saving || loading || isEditMode}
              />
            </div>

            {/* Return Type */}
            <div className="space-y-2">
              <Label htmlFor="returnType" className="text-gray-700 dark:text-gray-300 font-medium">
                Return Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.returnType}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    returnType: value,
                  }))
                }}
                disabled={saving || loading || isEditMode}
              >
                <SelectTrigger id="returnType">
                  <SelectValue placeholder="Select return type" />
                </SelectTrigger>
                <SelectContent searchable={false}>
                  {returnTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Note - Full Width */}
          <div className="space-y-2">
            <Label htmlFor="note" className="text-gray-700 dark:text-gray-300 font-medium">
              Note
            </Label>
            <Textarea
              id="note"
              value={formData.note}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  note: e.target.value,
                }))
              }}
              disabled={saving || loading || isEditMode}
              placeholder="Enter note"
              rows={3}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/purchases/payment-returns')}
              className="w-full sm:w-auto"
              disabled={saving}
            >
              Cancel
            </Button>
            {!isEditMode && (
            <Button
              type="button"
              onClick={handleSave}
              className="w-full sm:w-auto"
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Payment Return
                </>
              )}
            </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

